import { memo, useCallback, useRef, useState, useEffect } from 'react'
import {
  VStack,
  HStack,
  Text,
  Icon,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Box,
  Tooltip,
  Spinner,
  useColorModeValue,
  Divider,
  Badge,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Checkbox,
  Skeleton,
  Stack,
} from '@chakra-ui/react'
import {
  LuPlus,
  LuFileText,
  LuLink,
  LuUpload,
  LuFile,
  LuTrash2,
  LuRefreshCw,
  LuCheck,
  LuTriangleAlert,
  LuClock,
  LuBookmark,
  LuSearch,
  LuLibrary,
} from 'react-icons/lu'
import {
  useProjectSources,
  useUploadSource,
  useCreateNote,
  useCreateLink,
  useDeleteSource,
  useReprocessSource,
  useDeleteArticle,
} from '../../../../api/api_hooks/project-sources'
import axiosClient from '../../../../api/axios'

interface ProjectSourcesSidebarProps {
  projectId: string
  onSourceSelect?: (sourceId: string) => void
  selectedSourceId?: string | null
}

export const ProjectSourcesSidebar = memo(function ProjectSourcesSidebar({
  projectId,
  onSourceSelect,
  selectedSourceId,
}: ProjectSourcesSidebarProps) {
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, refetch } = useProjectSources(projectId)
  const uploadSource = useUploadSource()
  const createNote = useCreateNote()
  const createLink = useCreateLink()
  const deleteSource = useDeleteSource()
  const reprocessSource = useReprocessSource()
  const deleteArticle = useDeleteArticle()

  const noteModal = useDisclosure()
  const linkModal = useDisclosure()
  const literatureModal = useDisclosure()

  const [noteForm, setNoteForm] = useState({ title: '', content: '' })
  const [linkForm, setLinkForm] = useState({ title: '', url: '' })
  const [articles, setArticles] = useState<any[]>([])
  const [isLoadingArticles, setIsLoadingArticles] = useState(false)

  // Literature search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set())

  const cardBg = useColorModeValue('white', 'gray.700')
  const hoverBg = useColorModeValue('gray.100', 'gray.600')
  const selectedBg = useColorModeValue('blue.50', 'blue.900')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const sources = data?.sources || []

  // Fetch project articles
  useEffect(() => {
    const fetchArticles = async () => {
      setIsLoadingArticles(true)
      try {
        const response = await axiosClient.get(`/medinsight/projects/${projectId}/articles`)
        setArticles(response.data.articles || [])
      } catch (error) {
        console.error('Error fetching articles:', error)
      } finally {
        setIsLoadingArticles(false)
      }
    }
    fetchArticles()
  }, [projectId])

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      try {
        await uploadSource.mutateAsync({ projectId, file })
        toast({
          title: 'Fichier uploadé',
          description: 'Le fichier est en cours de traitement...',
          status: 'success',
          duration: 3000,
        })
        refetch()
      } catch (error: any) {
        toast({
          title: 'Erreur',
          description: error.message || 'Échec de l\'upload',
          status: 'error',
          duration: 5000,
        })
      }
    },
    [projectId, uploadSource, toast, refetch]
  )

  const handleCreateNote = useCallback(async () => {
    if (!noteForm.title || !noteForm.content) {
      toast({
        title: 'Champs requis',
        description: 'Veuillez remplir le titre et le contenu',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      await createNote.mutateAsync({ projectId, data: noteForm })
      toast({
        title: 'Note créée',
        status: 'success',
        duration: 3000,
      })
      setNoteForm({ title: '', content: '' })
      noteModal.onClose()
      refetch()
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        status: 'error',
        duration: 5000,
      })
    }
  }, [noteForm, projectId, createNote, toast, noteModal, refetch])

  const handleCreateLink = useCallback(async () => {
    if (!linkForm.title || !linkForm.url) {
      toast({
        title: 'Champs requis',
        description: 'Veuillez remplir le titre et l\'URL',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      await createLink.mutateAsync({ projectId, data: linkForm })
      toast({
        title: 'Lien ajouté',
        status: 'success',
        duration: 3000,
      })
      setLinkForm({ title: '', url: '' })
      linkModal.onClose()
      refetch()
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        status: 'error',
        duration: 5000,
      })
    }
  }, [linkForm, projectId, createLink, toast, linkModal, refetch])

  const handleDeleteSource = useCallback(
    async (sourceId: string) => {
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette source ?')) return

      try {
        await deleteSource.mutateAsync(sourceId)
        toast({
          title: 'Source supprimée',
          status: 'success',
          duration: 3000,
        })
        refetch()
      } catch (error: any) {
        toast({
          title: 'Erreur',
          description: error.message,
          status: 'error',
          duration: 5000,
        })
      }
    },
    [deleteSource, toast, refetch]
  )

  const handleReprocess = useCallback(
    async (sourceId: string) => {
      try {
        await reprocessSource.mutateAsync(sourceId)
        toast({
          title: 'Retraitement lancé',
          status: 'success',
          duration: 3000,
        })
        refetch()
      } catch (error: any) {
        toast({
          title: 'Erreur',
          description: error.message,
          status: 'error',
          duration: 5000,
        })
      }
    },
    [reprocessSource, toast, refetch]
  )

  const handleDeleteArticle = useCallback(
    async (articleId: string) => {
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article du projet ?')) return

      try {
        await deleteArticle.mutateAsync(articleId)
        toast({
          title: 'Article supprimé',
          status: 'success',
          duration: 3000,
        })
        // Refresh articles list
        const response = await axiosClient.get(`/medinsight/projects/${projectId}/articles`)
        setArticles(response.data.articles || [])
      } catch (error: any) {
        toast({
          title: 'Erreur',
          description: error.message || 'Impossible de supprimer l\'article',
          status: 'error',
          duration: 5000,
        })
      }
    },
    [deleteArticle, toast, projectId]
  )

  const handleSearchLiterature = useCallback(async () => {
    if (!searchQuery.trim()) {
      toast({
        title: 'Champ requis',
        description: 'Veuillez entrer une requête de recherche',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    setIsSearching(true)
    try {
      const response = await axiosClient.post('literature/search', {
        query: searchQuery.trim(),
        maxResults: 20,
        page: 1,
      })

      setSearchResults(response.data.results || [])
      toast({
        title: `${response.data.count || 0} articles trouvés`,
        status: 'success',
        duration: 3000,
      })
    } catch (error: any) {
      toast({
        title: 'Erreur de recherche',
        description: error.message || 'Impossible de rechercher les articles',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSearching(false)
    }
  }, [searchQuery, toast])

  const handleAddSelectedArticles = useCallback(async () => {
    if (selectedArticles.size === 0) {
      toast({
        title: 'Aucun article sélectionné',
        description: 'Veuillez sélectionner au moins un article',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    const articlesToAdd = searchResults.filter((article) =>
      selectedArticles.has(article.pubmedId || article.doi || article.title)
    )

    let successCount = 0
    let errorCount = 0

    for (const article of articlesToAdd) {
      try {
        await axiosClient.post(`medinsight/projects/${projectId}/articles`, {
          article: {
            title: article.title,
            abstract: article.abstract,
            authors: article.authors,
            journal: article.journal,
            publicationDate: article.publicationDate,
            doi: article.doi,
            pubmedId: article.pubmedId,
            pmid: article.pubmedId,
            externalId: article.pubmedId,
            url: article.url,
            keywords: article.keywords,
          },
        })
        successCount++
      } catch (error) {
        errorCount++
      }
    }

    if (successCount > 0) {
      toast({
        title: `${successCount} article(s) ajouté(s)`,
        status: 'success',
        duration: 3000,
      })
      refetch()
    }

    if (errorCount > 0) {
      toast({
        title: `${errorCount} article(s) non ajouté(s)`,
        description: 'Certains articles existent peut-être déjà dans le projet',
        status: 'warning',
        duration: 3000,
      })
    }

    setSelectedArticles(new Set())
    setSearchResults([])
    setSearchQuery('')
    literatureModal.onClose()
  }, [selectedArticles, searchResults, projectId, toast, refetch, literatureModal])

  const getSourceIcon = (sourceType: string) => {
    const icons: Record<string, any> = {
      pdf: LuFile,
      doc: LuFile,
      ppt: LuFile,
      csv: LuFile,
      xlsx: LuFile,
      note: LuFileText,
      link: LuLink,
      article: LuFileText,
    }
    return icons[sourceType] || LuFile
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Icon as={LuCheck} color="green.500" />
      case 'processing':
        return <Spinner size="xs" color="blue.500" />
      case 'failed':
        return <Icon as={LuTriangleAlert} color="red.500" />
      default:
        return <Icon as={LuClock} color="gray.500" />
    }
  }

  const totalSources = sources.length + articles.length

  return (
    <VStack spacing={0} h="full" overflow="hidden">
      {/* Header */}
      <HStack
        justify="space-between"
        p={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
        w="full"
      >
        <Text fontWeight="semibold">Sources ({totalSources})</Text>
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<LuPlus />}
            size="sm"
            colorScheme="blue"
            variant="ghost"
            aria-label="Ajouter une source"
          />
          <MenuList>
            <MenuItem
              icon={<LuUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Uploader un fichier
            </MenuItem>
            <MenuItem icon={<LuLibrary />} onClick={literatureModal.onOpen}>
              Rechercher dans la littérature
            </MenuItem>
            <MenuItem icon={<LuFileText />} onClick={noteModal.onOpen}>
              Créer une note
            </MenuItem>
            <MenuItem icon={<LuLink />} onClick={linkModal.onOpen}>
              Ajouter un lien
            </MenuItem>
          </MenuList>
        </Menu>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".pdf,.doc,.docx,.ppt,.pptx,.csv,.xlsx"
          onChange={handleFileUpload}
        />
      </HStack>

      {/* Sources List */}
      <VStack spacing={0} flex={1} overflow="auto" w="full" p={2}>
        {isLoading ? (
          <Box p={4}>
            <Spinner />
          </Box>
        ) : sources.length === 0 && articles.length === 0 ? (
          <Box p={4} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              Aucune source
            </Text>
            <Text color="gray.400" fontSize="xs" mt={2}>
              Ajoutez des fichiers, notes ou liens
            </Text>
          </Box>
        ) : (
          <>
            {/* Fichiers, notes, liens */}
            {sources.length > 0 && (
              <>
                {sources.map((source) => (
                  <Box
                    key={source.id}
                    w="full"
                    p={3}
                    bg={selectedSourceId === source.id ? selectedBg : cardBg}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={selectedSourceId === source.id ? 'blue.300' : borderColor}
                    mb={2}
                    cursor="pointer"
                    _hover={{ bg: hoverBg }}
                    onClick={() => onSourceSelect?.(source.id)}
                  >
                    <VStack align="start" spacing={2}>
                      <HStack justify="space-between" w="full">
                        <HStack spacing={2} flex={1} minW={0}>
                          <Icon as={getSourceIcon(source.sourceType)} />
                          <Text fontSize="sm" fontWeight="medium" isTruncated>
                            {source.title}
                          </Text>
                        </HStack>
                        <HStack spacing={1}>
                          {getStatusIcon(source.processingStatus)}
                          {source.processingStatus === 'failed' && (
                            <Tooltip label="Retraiter">
                              <IconButton
                                aria-label="Reprocess"
                                icon={<LuRefreshCw />}
                                size="xs"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleReprocess(source.id)
                                }}
                              />
                            </Tooltip>
                          )}
                          <Tooltip label="Supprimer">
                            <IconButton
                              aria-label="Delete"
                              icon={<LuTrash2 />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteSource(source.id)
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      </HStack>

                      {source.fileSize && (
                        <Text fontSize="xs" color="gray.500">
                          {(source.fileSize / 1024 / 1024).toFixed(2)} MB
                        </Text>
                      )}

                      {source.processingStatus === 'failed' && source.processingError && (
                        <Text fontSize="xs" color="red.500" isTruncated>
                          {source.processingError}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                ))}
              </>
            )}

            {/* Articles bookmarkés */}
            {articles.length > 0 && (
              <>
                {sources.length > 0 && <Divider my={4} />}

                <HStack w="full" px={2} mb={2}>
                  <Icon as={LuBookmark} color="purple.500" />
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                    Articles bookmarkés ({articles.length})
                  </Text>
                </HStack>

                {articles.map((article) => (
                  <Box
                    key={article.id}
                    w="full"
                    p={3}
                    bg={selectedSourceId === `article-${article.id}` ? selectedBg : cardBg}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={selectedSourceId === `article-${article.id}` ? 'blue.300' : borderColor}
                    mb={2}
                    cursor="pointer"
                    _hover={{ bg: hoverBg }}
                    onClick={() => onSourceSelect?.(`article-${article.id}`)}
                  >
                    <VStack align="start" spacing={2}>
                      <HStack justify="space-between" w="full">
                        <HStack spacing={2} flex={1} minW={0}>
                          <Icon as={LuFileText} color="purple.500" />
                          <Text fontSize="sm" fontWeight="medium" isTruncated>
                            {article.title}
                          </Text>
                        </HStack>
                        <Tooltip label="Supprimer">
                          <IconButton
                            aria-label="Delete article"
                            icon={<LuTrash2 />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteArticle(article.id)
                            }}
                          />
                        </Tooltip>
                      </HStack>

                      {article.authors && article.authors.length > 0 && (
                        <Text fontSize="xs" color="gray.500" isTruncated>
                          {article.authors.slice(0, 3).join(', ')}
                          {article.authors.length > 3 ? ' et al.' : ''}
                        </Text>
                      )}

                      <HStack spacing={2} flexWrap="wrap">
                        {article.journal && (
                          <Badge colorScheme="purple" fontSize="xs">
                            {article.journal}
                          </Badge>
                        )}
                        {article.year && (
                          <Badge colorScheme="gray" fontSize="xs">
                            {article.year}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </>
            )}

            {isLoadingArticles && (
              <Box p={4}>
                <Spinner size="sm" />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Chargement des articles...
                </Text>
              </Box>
            )}
          </>
        )}
      </VStack>

      {/* Create Note Modal */}
      <Modal isOpen={noteModal.isOpen} onClose={noteModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Créer une note</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Titre</FormLabel>
                <Input
                  value={noteForm.title}
                  onChange={(e) =>
                    setNoteForm({ ...noteForm, title: e.target.value })
                  }
                  placeholder="Titre de la note"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Contenu</FormLabel>
                <Textarea
                  value={noteForm.content}
                  onChange={(e) =>
                    setNoteForm({ ...noteForm, content: e.target.value })
                  }
                  placeholder="Contenu de la note (Markdown supporté)"
                  rows={8}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={noteModal.onClose}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateNote}
              isLoading={createNote.isPending}
            >
              Créer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Link Modal */}
      <Modal isOpen={linkModal.isOpen} onClose={linkModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ajouter un lien</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Titre</FormLabel>
                <Input
                  value={linkForm.title}
                  onChange={(e) =>
                    setLinkForm({ ...linkForm, title: e.target.value })
                  }
                  placeholder="Titre du lien"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>URL</FormLabel>
                <Input
                  value={linkForm.url}
                  onChange={(e) =>
                    setLinkForm({ ...linkForm, url: e.target.value })
                  }
                  placeholder="https://..."
                  type="url"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={linkModal.onClose}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateLink}
              isLoading={createLink.isPending}
            >
              Ajouter
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Literature Search Modal */}
      <Modal
        isOpen={literatureModal.isOpen}
        onClose={() => {
          literatureModal.onClose()
          setSearchResults([])
          setSearchQuery('')
          setSelectedArticles(new Set())
        }}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" borderRadius="2xl">
          <ModalHeader
            bgGradient="linear(135deg, purple.500 0%, pink.400 100%)"
            color="white"
            borderTopRadius="2xl"
          >
            <HStack spacing={2}>
              <Icon as={LuLibrary} />
              <Text>Rechercher dans la littérature</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6} overflowY="auto">
            <VStack spacing={4} align="stretch">
              {/* Search Bar */}
              <HStack spacing={2}>
                <InputGroup size="lg" flex={1}>
                  <InputLeftElement>
                    <Icon as={LuSearch} color="purple.500" />
                  </InputLeftElement>
                  <Input
                    placeholder="Rechercher des articles (PubMed, Semantic Scholar)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchLiterature()}
                    borderRadius="xl"
                  />
                </InputGroup>
                <Button
                  colorScheme="purple"
                  size="lg"
                  onClick={handleSearchLiterature}
                  isLoading={isSearching}
                  borderRadius="xl"
                  px={8}
                >
                  Rechercher
                </Button>
              </HStack>

              {/* Results */}
              {isSearching ? (
                <Stack spacing={3}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height="120px" borderRadius="xl" />
                  ))}
                </Stack>
              ) : searchResults.length > 0 ? (
                <>
                  <HStack justify="space-between" px={2}>
                    <Text fontSize="sm" color="gray.600">
                      {searchResults.length} articles trouvés • {selectedArticles.size} sélectionné(s)
                    </Text>
                    {selectedArticles.size > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedArticles(new Set())}
                      >
                        Tout désélectionner
                      </Button>
                    )}
                  </HStack>

                  <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
                    {searchResults.map((article, index) => {
                      const articleKey = article.pubmedId || article.doi || article.title
                      const isSelected = selectedArticles.has(articleKey)

                      return (
                        <Card
                          key={index}
                          variant="outline"
                          borderWidth="2px"
                          borderColor={isSelected ? 'purple.300' : borderColor}
                          bg={isSelected ? 'purple.50' : cardBg}
                          cursor="pointer"
                          onClick={() => {
                            const newSelected = new Set(selectedArticles)
                            if (isSelected) {
                              newSelected.delete(articleKey)
                            } else {
                              newSelected.add(articleKey)
                            }
                            setSelectedArticles(newSelected)
                          }}
                          _hover={{ borderColor: 'purple.400', shadow: 'md' }}
                          transition="all 0.2s"
                          borderRadius="xl"
                        >
                          <CardBody>
                            <HStack align="start" spacing={3}>
                              <Checkbox
                                isChecked={isSelected}
                                colorScheme="purple"
                                size="lg"
                                mt={1}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  const newSelected = new Set(selectedArticles)
                                  if (e.target.checked) {
                                    newSelected.add(articleKey)
                                  } else {
                                    newSelected.delete(articleKey)
                                  }
                                  setSelectedArticles(newSelected)
                                }}
                              />
                              <VStack align="start" spacing={2} flex={1}>
                                <Text fontWeight="bold" fontSize="md" noOfLines={2}>
                                  {article.title}
                                </Text>
                                {article.authors && article.authors.length > 0 && (
                                  <Text fontSize="sm" color="gray.600" noOfLines={1}>
                                    {article.authors.slice(0, 3).join(', ')}
                                    {article.authors.length > 3 ? ' et al.' : ''}
                                  </Text>
                                )}
                                <HStack spacing={2} flexWrap="wrap">
                                  {article.journal && (
                                    <Badge colorScheme="purple" fontSize="xs">
                                      {article.journal}
                                    </Badge>
                                  )}
                                  {article.publicationDate && (
                                    <Badge colorScheme="gray" fontSize="xs">
                                      {new Date(article.publicationDate).getFullYear()}
                                    </Badge>
                                  )}
                                  {article.pubmedId && (
                                    <Badge colorScheme="blue" fontSize="xs">
                                      PMID: {article.pubmedId}
                                    </Badge>
                                  )}
                                </HStack>
                                {article.abstract && (
                                  <Text fontSize="xs" color="gray.600" noOfLines={2}>
                                    {article.abstract}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </CardBody>
                        </Card>
                      )
                    })}
                  </VStack>
                </>
              ) : searchQuery && !isSearching ? (
                <Box textAlign="center" py={8}>
                  <Icon as={LuSearch} boxSize={12} color="gray.300" mb={3} />
                  <Text color="gray.500">Aucun résultat trouvé</Text>
                  <Text fontSize="sm" color="gray.400" mt={1}>
                    Essayez une autre recherche
                  </Text>
                </Box>
              ) : (
                <Box textAlign="center" py={8}>
                  <Icon as={LuLibrary} boxSize={12} color="gray.300" mb={3} />
                  <Text color="gray.500">Commencez votre recherche</Text>
                  <Text fontSize="sm" color="gray.400" mt={1}>
                    Recherchez des articles scientifiques pour les ajouter à votre projet
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                literatureModal.onClose()
                setSearchResults([])
                setSearchQuery('')
                setSelectedArticles(new Set())
              }}
              borderRadius="lg"
            >
              Annuler
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleAddSelectedArticles}
              isDisabled={selectedArticles.size === 0}
              borderRadius="lg"
              leftIcon={<Icon as={LuPlus} />}
            >
              Ajouter {selectedArticles.size > 0 ? `(${selectedArticles.size})` : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
})
